import React, { useEffect, useRef, useState } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useDispatch, useSelector } from 'react-redux';
import {
  useGetKnowledgeBaseStatusQuery,
  useGetUserDataQuery,
  useMarkWalkthroughSeenMutation,
} from '../services/auth';
import { setWalkthroughOpen } from '../services/GlobalSlice';
import {
  isWalkthroughDismissedThisSession,
  markWalkthroughDismissedThisSession,
  shouldShowWalkthrough,
} from './walkthroughVisibility';
import './Walkthrough.css';

const getSteps = (hasDocuments) => [
  {
    title: 'What Edge AI knows',
    body: hasDocuments
      ? "Edge AI answers Oracle HCM questions using Oracle's release documentation and your workspace's implementation documents."
      : "Edge AI answers Oracle HCM questions using Oracle's release documentation. Your workspace admin can add your implementation documents so answers match your setup.",
  },
  {
    title: 'Ask a question, check the sources',
    body: 'Ask in plain language, for example: What is a legal employer in Oracle HCM? When an answer is based on your workspace documents, the sources appear under it with a relevance score. Answers from general Oracle knowledge are labelled as such.',
  },
  {
    title: 'Bring your own document',
    body: 'Use the attach button to analyze a PDF or Excel file in a chat. The file is read for that question and is not kept. Inside a conversation you can also attach a reference document that Edge AI keeps in mind for every question in that chat.',
  },
  {
    title: 'Rate answers',
    body: 'Give any answer a thumbs up or thumbs down. Your feedback is reviewed by your admins. You can reopen this walkthrough any time from the account menu.',
  },
];

const Walkthrough = () => {
  const dispatch = useDispatch();
  const openedFromMenu = useSelector((state) => state.global.walkthroughOpen);
  const { data: user } = useGetUserDataQuery();
  const [markSeen] = useMarkWalkthroughSeenMutation();
  const [autoOpen, setAutoOpen] = useState(false);
  const [step, setStep] = useState(0);
  const autoShownRef = useRef(false);
  const returnFocusToMenuRef = useRef(false);
  const primaryRef = useRef(null);

  const open = autoOpen || openedFromMenu;
  // Neutral wording until this resolves, so a slow or failed request never
  // claims documents the workspace does not have.
  const { data: kbStatus } = useGetKnowledgeBaseStatusQuery(undefined, { skip: !open });
  const steps = getSteps(kbStatus?.has_documents === true);
  const current = steps[step];
  const isLast = step === steps.length - 1;

  useEffect(() => {
    if (autoShownRef.current || !user) return;
    if (
      shouldShowWalkthrough(user, user.can_ask !== false) &&
      !isWalkthroughDismissedThisSession(user.id)
    ) {
      autoShownRef.current = true;
      setAutoOpen(true);
    }
  }, [user]);

  const dismiss = () => {
    returnFocusToMenuRef.current = openedFromMenu && !autoOpen;
    setAutoOpen(false);
    dispatch(setWalkthroughOpen(false));
    setStep(0);
    if (!user || user.walkthrough_seen_at !== null) return;
    markSeen()
      .unwrap()
      .catch(() => markWalkthroughDismissedThisSession(user.id));
  };

  const goBack = () => {
    setStep((s) => s - 1);
    // Back is not rendered on the first step, so focus would drop to the body.
    if (step === 1) window.requestAnimationFrame(() => primaryRef.current?.focus());
  };

  return (
    <DialogPrimitive.Root open={open} onOpenChange={(next) => { if (!next) dismiss(); }}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="wt-overlay" />
        <DialogPrimitive.Content
          className="wt-content"
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            primaryRef.current?.focus();
          }}
          onCloseAutoFocus={(e) => {
            if (!returnFocusToMenuRef.current) return;
            e.preventDefault();
            document.getElementById('user-dropdown')?.focus();
          }}
          onInteractOutside={(e) => e.preventDefault()}
        >
          <DialogPrimitive.Title className="wt-title">Welcome to Edge AI</DialogPrimitive.Title>
          <h3 className="wt-step-title">{current.title}</h3>
          <DialogPrimitive.Description className="wt-step-body">
            {current.body}
          </DialogPrimitive.Description>
          <div className="wt-dots" aria-hidden="true">
            {steps.map((s, i) => (
              <span key={s.title} className={`wt-dot ${i === step ? 'is-active' : ''}`} />
            ))}
          </div>
          <p className="wt-sr-only" aria-live="polite">
            {`Step ${step + 1} of ${steps.length}. ${current.title}`}
          </p>
          <div className="wt-actions">
            <button type="button" className="wt-btn wt-btn-ghost wt-btn-skip" onClick={dismiss}>
              Skip
            </button>
            {step > 0 && (
              <button type="button" className="wt-btn wt-btn-ghost" onClick={goBack}>
                Back
              </button>
            )}
            <button
              ref={primaryRef}
              type="button"
              className="wt-btn wt-btn-primary"
              onClick={isLast ? dismiss : () => setStep((s) => s + 1)}
            >
              {isLast ? 'Done' : 'Next'}
            </button>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};

export default Walkthrough;
